import { useState, useMemo, MouseEvent, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Badge,
  Box,
  Button,
  IconButton,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TablePagination,
  TableSortLabel,
  TextField,
  Toolbar,
} from "@mui/material";
import { DateTime } from "luxon";
import { Order, stableSort, getComparator } from "utils/Table";
import AddIcon from "@mui/icons-material/Add";
import { visuallyHidden } from "@mui/utils";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SimpleListDialog from "components/SimpleListDialog";
import { IPackage, PackageState, wadToString } from "services/registry/PackageService";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PublishIcon from "@mui/icons-material/Publish";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";

interface HeadCell {
  id: keyof IPackage | string;
  label: string;
  align?: "inherit" | "left" | "center" | "right" | "justify";
  padding?: "normal" | "checkbox" | "none";
}

const headCells: readonly HeadCell[] = [
  {
    id: "id",
    label: "ID",
    padding: "none",
  },
  {
    id: "description",
    label: "Description",
  },
  {
    id: "wads",
    label: "Wads",
  },
  {
    id: "state",
    label: "State",
  },
  {
    id: "createdTime",
    label: "Creation Date",
  },
  {
    id: "publishedTime",
    label: "Published Date",
  },
  {
    id: "bucketPath",
    label: "S3 Bucket",
  },
  {
    id: "actions",
    label: "",
  },
];

interface PackagesTableHeadProps {
  onRequestSort: (event: MouseEvent<unknown>, property: keyof IPackage | string) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

const PackagesTableHead = ({ order, orderBy, onRequestSort }: PackagesTableHeadProps) => {
  const createSortHandler = (property: keyof IPackage | string) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox"></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.padding}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export interface PackagesTableProps {
  rows: IPackage[];
  selectedItem: IPackage | undefined;
  setSelectedItem: React.Dispatch<React.SetStateAction<IPackage | undefined>>;
  onAddButtonClick: () => void;
  onEditButtonClick: () => void;
  onRemoveButtonClick: (option: boolean) => void;
  onPublishButtonClick: (option: boolean) => void;
  onDuplicateButtonClick: (packId: string) => void;
  onViewButtonClick: (packId: string) => void;
  onWadSelect: (wadId: string) => void;
  initialFilterQuery?: string;
  onRefreshButtonClick: () => void;
}

export const PackagesTable = ({
  rows,
  selectedItem,
  setSelectedItem,
  onAddButtonClick,
  onEditButtonClick,
  onRemoveButtonClick,
  onPublishButtonClick,
  onDuplicateButtonClick,
  onViewButtonClick,
  onWadSelect,
  initialFilterQuery = "",
  onRefreshButtonClick,
}: PackagesTableProps) => {
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof IPackage | string>("createdTime");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openLabelDialog, setOpenLabelDialog] = useState(false);
  const [filterQuery, setFilterQuery] = useState<string>(initialFilterQuery);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<IPackage | null>(null);

  useEffect(() => {
    setFilterQuery(initialFilterQuery);
  }, [initialFilterQuery]);

  const getPackageDownloadUrl = (pack: IPackage): string | undefined => {
    if (!pack.bucketPath || !pack.id) {
      return undefined;
    }
    // Extract bucket name (part before first slash)
    const bucketName = pack.bucketPath.split("/")[0];
    // Extract path (everything after bucket name, including trailing slash)
    const path = pack.bucketPath.substring(bucketName.length + 1);
    return `https://${bucketName}.s3.us-east-1.amazonaws.com/${path}`;
  };

  const requestSortHandler = (event: MouseEvent<unknown>, property: keyof IPackage | string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const pageChangeHandler = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const rowsPerPageChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showPackageWads = (row: IPackage) => {
    setSelectedItem(row);
    setOpenLabelDialog(true);
  };

  const handleCloseLabelDialog = () => {
    setOpenLabelDialog(false);
  };

  const handleEditClick = (event: React.MouseEvent<unknown>, row: IPackage) => {
    setSelectedItem(row);
    onEditButtonClick();
    handleCloseMenu();
  };

  const handleRemoveClick = (row: IPackage) => {
    setSelectedItem(row);
    onRemoveButtonClick(true);
    handleCloseMenu();
  };

  const handlePublishClick = (row: IPackage) => {
    setSelectedItem(row);
    onPublishButtonClick(true);
    handleCloseMenu();
  };

  const handleDuplicateClick = (row: IPackage) => {
    setSelectedItem(row);
    onDuplicateButtonClick(row.id);
    handleCloseMenu();
  };

  const handleViewClick = (row: IPackage) => {
    setSelectedItem(row);
    onViewButtonClick(row.id);
    handleCloseMenu();
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: IPackage) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const query = filterQuery.toLowerCase();
      return (
        row.id.toLowerCase().includes(query) ||
        (row.description && row.description.toLowerCase().includes(query)) ||
        row.wads.some((wad) => wad.id.toLowerCase().includes(query))
      );
    });
  }, [rows, filterQuery]);

  const visibleRows = useMemo(() => {
    return stableSort(filteredRows, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredRows, order, orderBy, page, rowsPerPage]);

  const isSelected = (row: IPackage, selectedItem: IPackage | undefined) =>
    selectedItem !== undefined && row.id === selectedItem.id;

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2 }}>
          <TableContainer>
            <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddButtonClick}>
                Add
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={onRefreshButtonClick}
                style={{ marginLeft: 8 }}
              >
                Refresh
              </Button>
              <TextField
                id="outlined-search"
                label="Search packages"
                type="search"
                size="small"
                onChange={(e) => setFilterQuery(e.target.value)}
                sx={{ width: "360px", marginLeft: 1 }}
                value={filterQuery}
              />
            </Toolbar>
            <Table sx={{ minWidth: 750 }} size="small">
              <PackagesTableHead order={order} orderBy={orderBy} onRequestSort={requestSortHandler} rowCount={rows.length} />
              <TableBody>
                {visibleRows.map((row, index) => {
                  const labelId = `Packages-table-checkbox-${index}`;
                  const creationDate = DateTime.fromSeconds(row.createdTime).toLocaleString(
                    DateTime.DATETIME_SHORT_WITH_SECONDS,
                  );
                  const publishedDate = row.publishedTime
                    ? DateTime.fromSeconds(row.publishedTime).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
                    : "";

                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      onClick={() => setSelectedItem(row)}
                      tabIndex={-1}
                      key={row.id}
                      selected={isSelected(row, selectedItem)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="normal" />
                      <TableCell component="th" id={labelId} scope="row" padding="none">
                        <Link href={getPackageDownloadUrl(row)} target="_blank" rel="noopener noreferrer">
                          {row.id}
                        </Link>
                      </TableCell>
                      <TableCell align="left">
                        {row.description && row.description.length > 30
                          ? `${row.description.substring(0, 30)}...`
                          : row.description}
                      </TableCell>
                      <TableCell align="left">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            showPackageWads(row);
                          }}
                        >
                          <Badge badgeContent={row.wads.length ?? 0} color="primary" showZero>
                            <ListAltIcon fontSize="inherit" />
                          </Badge>
                        </IconButton>
                      </TableCell>
                      <TableCell align="left">{row.state}</TableCell>
                      <TableCell align="left">{creationDate}</TableCell>
                      <TableCell align="left">{publishedDate}</TableCell>
                      <TableCell align="left">{row.bucketPath?.substring(0, row.bucketPath.lastIndexOf("/") + 1)}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={(event) => handleOpenMenu(event, row)} size="small">
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 33 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={pageChangeHandler}
            onRowsPerPageChange={rowsPerPageChangeHandler}
          />
        </Paper>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={(event) => menuRow && handleEditClick(event, menuRow)}
          disabled={menuRow?.state === PackageState.PUBLISHED}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuRow && handleViewClick(menuRow)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Content</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuRow && handleDuplicateClick(menuRow)}>
          <ListItemIcon>
            <FileCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => menuRow && handlePublishClick(menuRow)}
          disabled={menuRow?.state === PackageState.PUBLISHED}
        >
          <ListItemIcon>
            <PublishIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Publish</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuRow && handleRemoveClick(menuRow)} disabled={menuRow?.state === PackageState.PUBLISHED}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>
      <SimpleListDialog
        title="Wads"
        listItems={selectedItem?.wads.map((wad) => wadToString(wad)) ?? []}
        emptyListMessage="No files to display"
        open={openLabelDialog}
        onClose={handleCloseLabelDialog}
        onSelect={(wadLabel) => onWadSelect(wadLabel.split(" | ")[0])}
      />
    </>
  );
};
